import { useCallback, useEffect, useRef } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react'

const FOCUSABLE =
  'button:not(:disabled):not([tabindex="-1"]), input:not(:disabled):not([type="hidden"]):not([tabindex="-1"]), select:not(:disabled):not([tabindex="-1"]), textarea:not(:disabled):not([tabindex="-1"]), a[href]:not([tabindex="-1"]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])'

const modalStack: symbol[] = []

export interface ModalDialogOptions {
  /** 组件可以常驻，只在真正显示时接管与恢复焦点。 */
  readonly open: boolean
  /** 省略即表示这个模态不能靠 Esc 退出。 */
  readonly onClose?: () => void
  readonly dismissible?: boolean
}

export interface ModalDialogBinding {
  readonly dialogRef: RefObject<HTMLDivElement | null>
  onDialogKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void
}

/**
 * 全应用模态的共同键盘契约：打开后接管焦点，Tab 留在面板内，Esc 退出，
 * 关闭后把焦点还给入口。视觉外壳仍由各业务组件决定。
 */
export function useModalDialog({
  open,
  onClose,
  dismissible = true,
}: ModalDialogOptions): ModalDialogBinding {
  const dialogRef = useRef<HTMLDivElement>(null)
  const stackToken = useRef(Symbol('modal-dialog'))

  useEffect(() => {
    if (!open) return undefined
    const doc = (globalThis as { document?: Document }).document
    if (doc === undefined) return undefined

    const previous = doc.activeElement
    const token = stackToken.current
    modalStack.push(token)
    const frame = globalThis.requestAnimationFrame(() => {
      const dialog = dialogRef.current
      if (dialog !== null && !dialog.contains(doc.activeElement)) dialog.focus()
    })

    return () => {
      globalThis.cancelAnimationFrame(frame)
      const index = modalStack.lastIndexOf(token)
      if (index >= 0) modalStack.splice(index, 1)
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open || !dismissible || onClose === undefined) return undefined
    const doc = (globalThis as { document?: Document }).document
    if (doc === undefined) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || modalStack[modalStack.length - 1] !== stackToken.current) return
      event.preventDefault()
      event.stopPropagation()
      onClose()
    }
    doc.addEventListener('keydown', onKeyDown)
    return () => doc.removeEventListener('keydown', onKeyDown)
  }, [dismissible, onClose, open])

  const onDialogKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Tab') return
    const dialog = dialogRef.current
    if (dialog === null) return

    const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((item) => {
      if (item.closest('[hidden], [inert], [aria-hidden="true"]') !== null) return false
      return item.getClientRects().length > 0
    })
    if (items.length === 0) {
      event.preventDefault()
      dialog.focus()
      return
    }

    const first = items[0]
    const last = items[items.length - 1]
    const active = dialog.ownerDocument.activeElement
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first?.focus()
    }
  }, [])

  return { dialogRef, onDialogKeyDown }
}
